import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, OnInit } from "@angular/core";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ProgressCategory } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressProduct } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ImportProgressProductsDialogComponent } from "@common/progress/import-progress-products-dialog/import-progress-products-dialog.component";
import { ProgressService } from "@common/progress/progress.service";
import { PageRouteBuilder } from "@common/route/page-route-builder";
import { PreventNavigationGuard } from "@common/route/prevent-navigation.guard";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { OrganisationsService } from "app/features/organisations/organisations.service";
import { NimbusDataService } from "app/services/nimbus-data.service";
import { ReorderEvent } from "devextreme/ui/sortable";
import { defer, lastValueFrom, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Component({
    selector: "adapt-progress-list-page",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./progress-list-page.component.html",
    styleUrl: "./progress-list-page.component.scss",
})
export class ProgressListPageComponent extends BaseRoutedComponent implements OnInit, OnDestroy {
    public progressProducts: ProgressProduct[] = [];

    private removePreventNavigationCallback?: () => void;

    public constructor(
        injector: Injector,
        elementRef: ElementRef<HTMLElement>,
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        private nimbusDataService: NimbusDataService,
        private organisationsService: OrganisationsService,
        private progressService: ProgressService,
        private preventNavigationGuard: PreventNavigationGuard,
    ) {
        super(injector, elementRef);

        this.removePreventNavigationCallback = this.preventNavigationGuard.registerCanNavigateCallback(() => defer(() => {
            // disable navigation when there are unsaved changes
            if (!this.commonDataService.hasChanges()) {
                return of(true);
            }

            // otherwise confirm to discard changes
            return this.dialogService.openConfirmDiscardDialog().pipe(
                map((result) => !!result),
            );
        }));
    }

    public async ngOnInit() {
        // disable save overlay for progress items
        this.commonDataService.disableSaveOverlayForEntityTypes(ProgressItem);

        await this.updateData();

        this.notifyActivated();
    }

    public get hasChanges() {
        return this.commonDataService.hasChanges();
    }

    public ngOnDestroy() {
        // re-enable save overlay for progress items
        this.commonDataService.enableSaveOverlayForEntityTypes(ProgressItem);

        // allow navigation again
        this.removePreventNavigationCallback?.();
        super.ngOnDestroy();
    }

    public onProductReorder(e: ReorderEvent) {
        // only reorder if actually changed
        if (e.fromIndex !== e.toIndex) {
            SortUtilities.reorderItemInIntegerSortedArray(this.progressProducts, "ordinal", e.fromIndex!, e.toIndex!);
        }

        this.commonDataService.saveEntities(this.progressProducts).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public onCategoryReorder(e: ReorderEvent, product: ProgressProduct) {
        // only reorder if actually changed
        if (e.fromIndex !== e.toIndex) {
            SortUtilities.reorderItemInIntegerSortedArray(product.progressCategories, "ordinal", e.fromIndex!, e.toIndex!);
        }

        this.commonDataService.saveEntities(product.progressCategories).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    @Autobind
    public createNewProgressProduct() {
        return this.nimbusDataService.getPartners().pipe(
            switchMap((partners) => this.progressService.createNewProgressProduct(this.progressProducts, undefined, partners)),
            switchMap(async (product) => {
                await this.updateData();
                ProgressService.scrollToNewProduct(this.elementRef?.nativeElement, product.progressProductId);
            }),
        );
    }

    @Autobind
    public editProgressProduct(product: ProgressProduct) {
        return this.nimbusDataService.getPartners().pipe(
            switchMap((partners) => this.progressService.editProgressProduct(product, this.progressProducts, partners)),
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public createNewProgressCategory(product: ProgressProduct) {
        return this.progressService.createNewProgressCategory(product).pipe(
            switchMap(async (category) => {
                await this.updateData();
                ProgressService.scrollToNewCategory(this.elementRef?.nativeElement, category.progressCategoryId);
            }),
        );
    }

    @Autobind
    public editProgressCategory(category: ProgressCategory) {
        return this.progressService.editProgressCategory(category).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public deleteProduct(product: ProgressProduct) {
        return this.progressService.deleteProduct(product, this.progressProducts).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public deleteCategory(category: ProgressCategory) {
        return this.progressService.deleteCategory(category).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public addProductsToOrganisation() {
        return defer(() => this.organisationsService.getOrganisationsWithAccounts(true)).pipe(
            switchMap((organisations) => this.dialogService.open(ImportProgressProductsDialogComponent, {
                organisations: organisations.sort(SortUtilities.getSortByFieldFunction<Organisation>("name")),
            })),
        );
    }

    private async updateData() {
        this.progressProducts = await lastValueFrom(this.progressService.getTemplateProgressProducts());
        for (const progressProduct of this.progressProducts) {
            progressProduct.progressCategories.sort(SortUtilities.getSortByFieldFunction<ProgressCategory>("ordinal"));
        }
    }
}

export const progressListPageRoute = new PageRouteBuilder()
    .usingNgComponent("adapt-progress-list-page", ProgressListPageComponent)
    .atUrl("/tools/progress-products")
    .withTitle("Journey Progress Modules")
    .build();
